import {useLayoutEffect, useRef, useState} from 'react';
import type {Section, SectionTransition} from '../hooks/useTasks.js';
import {useDialogs} from './DialogProvider.js';

type Rect = {left: number; top: number; right: number; bottom: number; width: number; height: number};
const workflowDotClass: Record<Section['workflowState'], string> = {todo: 'is-todo', in_progress: 'is-in-progress', done: 'is-done'};

export function SectionGraph({sections, transitions, onCreateTransition, onDeleteTransition}: {
  sections: Section[];
  transitions: SectionTransition[];
  onCreateTransition: (fromSectionId: string, toSectionId: string) => void;
  onDeleteTransition: (fromSectionId: string, toSectionId: string) => void;
}) {
  const {confirm} = useDialogs();
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLDivElement>());
  const [rects, setRects] = useState<Record<string, Rect>>({});
  const [drag, setDrag] = useState<{fromSectionId: string; x: number; y: number} | null>(null);

  useLayoutEffect(() => {
    function recalculate() {
      const container = containerRef.current;
      if (!container) return;
      const base = container.getBoundingClientRect();
      const next: Record<string, Rect> = {};
      for (const section of sections) {
        const el = nodeRefs.current.get(section.id);
        if (!el) continue;
        const box = el.getBoundingClientRect();
        next[section.id] = {left: box.left - base.left, top: box.top - base.top, right: box.right - base.left, bottom: box.bottom - base.top, width: box.width, height: box.height};
      }
      setRects(next);
    }
    recalculate();
    window.addEventListener('resize', recalculate);
    const observer = new ResizeObserver(recalculate);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => { window.removeEventListener('resize', recalculate); observer.disconnect(); };
  }, [sections]);

  function pointFromEvent(event: {clientX: number; clientY: number}) {
    const base = containerRef.current?.getBoundingClientRect();
    return {x: event.clientX - (base?.left ?? 0), y: event.clientY - (base?.top ?? 0)};
  }

  function startDrag(sectionId: string, event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromEvent(event);
    setDrag({fromSectionId: sectionId, x: point.x, y: point.y});

    function onMove(moveEvent: MouseEvent) {
      const next = pointFromEvent(moveEvent);
      setDrag(current => current ? {...current, x: next.x, y: next.y} : current);
    }
    function onUp(upEvent: MouseEvent) {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const target = document.elementFromPoint(upEvent.clientX, upEvent.clientY)?.closest('[data-section-id]') as HTMLElement | null;
      const targetId = target?.dataset.sectionId;
      setDrag(null);
      if (targetId && targetId !== sectionId) onCreateTransition(sectionId, targetId);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  async function removeTransition(transition: SectionTransition) {
    const fromName = sections.find(s => s.id === transition.fromSectionId)?.name ?? 'that section';
    const toName = sections.find(s => s.id === transition.toSectionId)?.name ?? 'that section';
    const ok = await confirm(`Remove the "${fromName}" → "${toName}" transition? Tasks will no longer be able to move directly between them.`, {confirmLabel: 'Remove Transition', danger: true});
    if (!ok) return;
    onDeleteTransition(transition.fromSectionId, transition.toSectionId);
  }

  function arcPath(from: Rect, to: Rect) {
    const forward = to.left >= from.left;
    const startX = forward ? from.right : from.left;
    const endX = forward ? to.left : to.right;
    const y = forward ? Math.min(from.top, to.top) : Math.max(from.bottom, to.bottom);
    const distance = Math.abs(endX - startX);
    const arc = forward ? -Math.max(30, distance * 0.2) : Math.max(30, distance * 0.2);
    const midX = (startX + endX) / 2;
    const controlY = y + arc;
    return {d: `M ${startX} ${y} Q ${midX} ${controlY} ${endX} ${y}`, markerEnd: forward};
  }

  return (
    <div className="section-graph" ref={containerRef}>
      <svg className="section-graph__svg">
        <defs>
          <marker id="section-graph-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--aw-gold-pale)"/>
          </marker>
        </defs>
        {transitions.map(transition => {
          const from = rects[transition.fromSectionId];
          const to = rects[transition.toSectionId];
          if (!from || !to) return null;
          const {d} = arcPath(from, to);
          return (
            <g key={`${transition.fromSectionId}-${transition.toSectionId}`} className="section-graph__edge">
              <path d={d} className="section-graph__edge-hit" onClick={() => { void removeTransition(transition); }}/>
              <path d={d} className="section-graph__edge-line" markerEnd="url(#section-graph-arrow)"/>
            </g>
          );
        })}
        {drag && rects[drag.fromSectionId] && (() => {
          const from = rects[drag.fromSectionId];
          const startX = drag.x >= from.left ? from.right : from.left;
          const y = (from.top + from.bottom) / 2;
          return <path d={`M ${startX} ${y} L ${drag.x} ${drag.y}`} className="section-graph__edge-preview"/>;
        })()}
      </svg>
      <div className="section-graph__row">
        {sections.map(section => (
          <div key={section.id} className="section-graph__node" data-section-id={section.id} ref={el => { if (el) nodeRefs.current.set(section.id, el); else nodeRefs.current.delete(section.id); }}>
            <i className={`task-section__dot ${workflowDotClass[section.workflowState]}`} aria-hidden="true"/>
            <span>{section.name}</span>
            <button type="button" className="section-graph__handle" aria-label={`Drag to draw an allowed transition from ${section.name}`} onMouseDown={event => startDrag(section.id, event)}/>
          </div>
        ))}
      </div>
    </div>
  );
}
